import type { GridlineShape } from "sketch/shapes/gridline";
import { formatPageUnits, pageToYUp } from "sketch/units";
/**
 * The gridline tool's sticker behavior: a ghost gridline rides the cursor
 * (with the label it will get), and a chip reads out where it sits, either
 * relative to the nearest parallel gridline or to the origin, so placement
 * is eyeballed first and locked down with dims afterward.
 *
 * The tool writes this atom; the OnTheCanvas overlay renders it. No store
 * records are involved, so peers and undo never see the ghost.
 */
import { atom } from "tldraw";
import { useEditor, useValue } from "tldraw";

export interface GridlineGhostState {
  axis: "x" | "y";
  x: number;
  y: number;
  label: string;
}

export const gridlineGhost = atom<GridlineGhostState | null>(
  "gridline ghost",
  null,
);

const BUBBLE_RADIUS = 14;
/** The placed line rules itself to the drawing; the ghost just needs to
 *  read as "a line through here" while aiming. */
const GHOST_HALF_LENGTH = 480;

function ghostContext(
  editor: ReturnType<typeof useEditor>,
  ghost: GridlineGhostState,
): string {
  const position = ghost.axis === "x" ? ghost.x : ghost.y;
  const parallels = editor
    .getCurrentPageShapes()
    .filter(
      (s): s is GridlineShape =>
        s.type === "gridline" && (s as GridlineShape).props.axis === ghost.axis,
    )
    .map((s) => ({
      label: s.props.label,
      at: ghost.axis === "x" ? s.x : s.y,
    }));
  if (parallels.length) {
    let nearest = parallels[0]!;
    for (const p of parallels) {
      if (Math.abs(p.at - position) < Math.abs(nearest.at - position)) {
        nearest = p;
      }
    }
    const delta = Math.abs(position - nearest.at);
    return delta < 1e-6
      ? `on @${nearest.label}`
      : `${formatPageUnits(delta)} from @${nearest.label}`;
  }
  const world = ghost.axis === "x" ? position : pageToYUp(position);
  return `${ghost.axis} = ${formatPageUnits(Math.abs(world)) || "0"}${world < 0 ? " (south/west of origin)" : ""}`;
}

export function GridlineGhost() {
  const editor = useEditor();
  const ghost = useValue(gridlineGhost);
  const context = useValue(
    "gridline ghost context",
    () => {
      const g = gridlineGhost.get();
      return g ? ghostContext(editor, g) : "";
    },
    [editor],
  );
  if (!ghost) return null;

  const vertical = ghost.axis === "x";
  const length = GHOST_HALF_LENGTH * 2;
  const left = vertical ? ghost.x - BUBBLE_RADIUS : ghost.x - GHOST_HALF_LENGTH;
  const top = vertical ? ghost.y - GHOST_HALF_LENGTH : ghost.y - BUBBLE_RADIUS;
  const width = vertical ? BUBBLE_RADIUS * 2 : length;
  const height = vertical ? length : BUBBLE_RADIUS * 2;

  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width,
        height,
        pointerEvents: "none",
        opacity: 0.55,
      }}
    >
      <svg width={width} height={height}>
        <line
          x1={vertical ? BUBBLE_RADIUS : 0}
          y1={vertical ? 0 : BUBBLE_RADIUS}
          x2={vertical ? BUBBLE_RADIUS : length}
          y2={vertical ? length : BUBBLE_RADIUS}
          stroke="var(--tl-color-selected)"
          strokeWidth={1}
          strokeDasharray="24 6 4 6"
        />
        <circle
          cx={BUBBLE_RADIUS}
          cy={BUBBLE_RADIUS}
          r={BUBBLE_RADIUS - 1}
          fill="var(--tl-color-background)"
          stroke="var(--tl-color-selected)"
          strokeWidth={1.25}
        />
        <text
          x={BUBBLE_RADIUS}
          y={BUBBLE_RADIUS}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={14}
          fontFamily="var(--tl-font-draw)"
          fill="var(--tl-color-selected)"
        >
          {ghost.label}
        </text>
      </svg>
      <div
        style={{
          position: "absolute",
          left: vertical ? BUBBLE_RADIUS + 10 : ghost.x - left + 10,
          top: vertical ? ghost.y - top - 8 : BUBBLE_RADIUS + 10,
          fontFamily: "var(--tl-font-draw)",
          fontSize: 12,
          whiteSpace: "nowrap",
          color: "var(--tl-color-selected)",
          background: "var(--tl-color-background)",
          border: "1px solid var(--tl-color-selected)",
          borderRadius: 3,
          padding: "1px 6px",
        }}
      >
        {context}
      </div>
    </div>
  );
}
